import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { IStructureTreePoint } from "../types";
import { sortPointsByUserSortNum } from "../utils/structure-tree-utils";

export interface IStructureTreeSelectionState {
  selectedPointIds: ReadonlySet<number>;
  selectedPoints: IStructureTreePoint[];
  selectedPointIdsArray: number[];
  togglePoint: (point: IStructureTreePoint, checked: boolean) => void;
  setTrainPointsSelected: (points: readonly IStructureTreePoint[], checked: boolean) => void;
  beginTrainPointsSelection: (trainId: number) => (points: readonly IStructureTreePoint[]) => void;
  resetSelection: () => void;
}

const removePointsFromOtherTrains = (map: Map<number, IStructureTreePoint>, trainId: number): void => {
  for (const [pointId, selectedPoint] of map.entries()) {
    if (selectedPoint.trainId !== trainId) {
      map.delete(pointId);
    }
  }
};

export const useStructureTreeSelection = ({
  isActive,
  selectedTrainId,
}: {
  isActive: boolean;
  selectedTrainId: number | null;
}): IStructureTreeSelectionState => {
  const [selectedPointsById, setSelectedPointsById] = useState<Map<number, IStructureTreePoint>>(() => new Map());
  const pendingTrainSelectionRef = useRef<{ trainId: number } | null>(null);
  const isActiveRef = useRef(isActive);

  useLayoutEffect(() => {
    isActiveRef.current = isActive;
    if (!isActive || pendingTrainSelectionRef.current?.trainId !== selectedTrainId) {
      pendingTrainSelectionRef.current = null;
    }
  }, [isActive, selectedTrainId]);

  useLayoutEffect(
    () => () => {
      isActiveRef.current = false;
      pendingTrainSelectionRef.current = null;
    },
    [],
  );

  const togglePoint = useCallback((point: IStructureTreePoint, checked: boolean) => {
    pendingTrainSelectionRef.current = null;
    setSelectedPointsById(previous => {
      const next = new Map(previous);

      if (checked) {
        if (point.trainId != null) {
          removePointsFromOtherTrains(next, point.trainId);
        }

        next.set(point.id, point);
      } else {
        next.delete(point.id);
      }

      return next;
    });
  }, []);

  const setTrainPointsSelected = useCallback((points: readonly IStructureTreePoint[], checked: boolean) => {
    pendingTrainSelectionRef.current = null;
    setSelectedPointsById(previous => {
      const next = new Map(previous);

      if (checked) {
        const trainId = points[0]?.trainId;

        if (trainId != null) {
          removePointsFromOtherTrains(next, trainId);
        }
      }

      points.forEach(point => {
        if (checked) {
          next.set(point.id, point);
        } else {
          next.delete(point.id);
        }
      });

      return next;
    });
  }, []);

  const beginTrainPointsSelection = useCallback(
    (trainId: number) => {
      const pendingSelection = { trainId };
      pendingTrainSelectionRef.current = isActiveRef.current ? pendingSelection : null;

      return (points: readonly IStructureTreePoint[]) => {
        if (pendingTrainSelectionRef.current === pendingSelection) {
          setTrainPointsSelected(points, true);
        }
      };
    },
    [setTrainPointsSelected],
  );

  const resetSelection = useCallback(() => {
    pendingTrainSelectionRef.current = null;
    setSelectedPointsById(new Map());
  }, []);

  const selectedPoints = useMemo(
    () => sortPointsByUserSortNum(Array.from(selectedPointsById.values())),
    [selectedPointsById],
  );

  const selectedPointIds = useMemo(() => new Set(selectedPoints.map(point => point.id)), [selectedPoints]);

  const selectedPointIdsArray = useMemo(() => selectedPoints.map(point => point.id), [selectedPoints]);

  return {
    selectedPointIds,
    selectedPoints,
    selectedPointIdsArray,
    togglePoint,
    setTrainPointsSelected,
    beginTrainPointsSelection,
    resetSelection,
  };
};
